<!--
// app/components/draggable/LineTierDraggableKwc.vue
-->
<script setup lang="ts">
type Props = {
  label: string;
  accentColor: string;
  isCanvasImage?: boolean;
};

const props = defineProps<Props>();
const emit = defineEmits<{ "update:label": [value: string] }>();

const editableRef = ref<HTMLDivElement | null>(null);

const labelFontClass = computed(() => {
  const len = props.label.length;
  if (len <= 3) return "text-xl bigsm:text-4xl";
  if (len <= 8) return "text-sm bigsm:text-2xl";
  if (len <= 16) return "text-xs bigsm:text-lg";
  return "text-xs bigsm:text-base";
});

const canvasFontClass = computed(() => {
  const len = props.label.length;
  if (len <= 3) return "text-4xl";
  if (len <= 8) return "text-2xl";
  if (len <= 16) return "text-lg";
  return "text-base";
});

function hexToRgba(hex: string, alpha: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

const labelCellStyle = computed(() => ({
  backgroundColor: hexToRgba(props.accentColor, 0.8),
  borderLeftColor: props.accentColor,
}));

onMounted(() => {
  if (editableRef.value) {
    editableRef.value.innerText = props.label;
  }
});

watch(
  () => props.label,
  newVal => {
    if (editableRef.value && editableRef.value.innerText !== newVal) {
      editableRef.value.innerText = newVal;
    }
  }
);

function onInput(e: Event) {
  const el = e.target as HTMLDivElement;
  const raw = el.innerText.replace(/\n/g, " ").trimEnd();
  if (raw.length > 25) {
    const trimmed = raw.slice(0, 25);
    el.innerText = trimmed;
    const range = document.createRange();
    range.selectNodeContents(el);
    range.collapse(false);
    window.getSelection()?.removeAllRanges();
    window.getSelection()?.addRange(range);
    emit("update:label", trimmed);
  } else {
    emit("update:label", raw);
  }
}

function onKeydown(e: KeyboardEvent) {
  if (e.key === "Enter") e.preventDefault();
}
</script>

<template>
  <div class="tier-row">
    <div v-if="!isCanvasImage" class="drag-row-handle">
      <svg viewBox="0 0 24 24" fill="currentColor" class="w-4 h-4">
        <circle cx="9" cy="5" r="1.5" />
        <circle cx="15" cy="5" r="1.5" />
        <circle cx="9" cy="12" r="1.5" />
        <circle cx="15" cy="12" r="1.5" />
        <circle cx="9" cy="19" r="1.5" />
        <circle cx="15" cy="19" r="1.5" />
      </svg>
    </div>
    <div
      :style="labelCellStyle"
      :class="['tier-label-cell', { 'sticky ltr:left-6 rtl:right-6': !isCanvasImage }]"
    >
      <div
        v-if="!isCanvasImage"
        ref="editableRef"
        contenteditable="true"
        role="textbox"
        @input="onInput"
        @keydown="onKeydown"
        :class="['tier-label-input', labelFontClass]"
      />
      <span
        v-else
        :class="['font-black text-black text-center w-full break-words leading-tight px-2', canvasFontClass]"
        >{{ label }}</span
      >
    </div>
    <slot />
  </div>
</template>

<style scoped>
.tier-row {
  @apply flex items-stretch min-h-[80px] relative;
  border-bottom: 3px solid rgba(212, 183, 142, 0.15);
}

.tier-row:last-child {
  border-bottom: none;
}

.drag-row-handle {
  @apply w-6 shrink-0 cursor-grab flex items-center justify-center bg-black text-gray-700;
  @apply sticky ltr:left-0 rtl:right-0 hover:text-gray-400 transition-colors select-none active:cursor-grabbing;
}

.tier-label-cell {
  @apply w-28 bigsm:w-52 shrink-0 flex items-center justify-center;
  border-left-width: 5px;
  border-left-style: solid;
}

.tier-label-input {
  @apply w-full text-black text-center font-black bg-transparent outline-none cursor-text px-2 py-1;
  @apply hover:bg-black/10 focus:bg-black/10 transition-colors;
  word-break: break-word;
  white-space: pre-wrap;
  align-self: center;
}
</style>
